import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Play, Shield, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import PaymentModal from '../components/PaymentModal';
import { StoriesPreview } from '../pages/StoriesPage';
import TestimonialsModal, { TestimonialsCTA } from '../components/TestimonialsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to pause all videos on the page (both Vimeo iframes and native video elements)
const pauseAllVideos = () => {
  // Pause Vimeo iframes
  const vimeoIframes = document.querySelectorAll('iframe[src*="vimeo"]');
  vimeoIframes.forEach((iframe) => {
    try {
      const src = iframe.src;
      if (src && src.includes('vimeo')) {
        iframe.contentWindow?.postMessage(JSON.stringify({
          method: 'pause'
        }), '*');
      }
    } catch {
      // Intentionally empty - video pause is non-critical UX enhancement
    }
  });
  
  // Pause native video elements (background video)
  const videoElements = document.querySelectorAll('.background-video-element');
  videoElements.forEach((video) => {
    try {
      video.pause();
    } catch {
      // Intentionally empty - video pause is non-critical UX enhancement
    }
  });
};

// Helper function to resume background video
const resumeBackgroundVideo = () => {
  const videoElements = document.querySelectorAll('.background-video-element');
  videoElements.forEach((video) => {
    try {
      video.play();
    } catch {
      // Intentionally empty - video resume is non-critical UX enhancement
    }
  });
};

const LandingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTestimonialsOpen, setIsTestimonialsOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Rotating quotes data
  const quotes = [
    {
      id: 'mick-fanning',
      text: "Watch the film, share it with your mates, and help the Northern Rivers community get back on their feet.",
      author: "Mick Fanning",
      title: "3x World Champion Surfer"
    },
    {
      id: 'ziggy-alberts',
      text: "I just came back from two years of world touring and I'm lost for words. I'm glad this documentary was made because it raises awareness of Australia's worst flood disaster.",
      author: "Ziggy Alberts",
      title: "Singer-Songwriter"
    }
  ];

  // Auto-rotate quotes every 6 seconds
  useEffect(() => {
    const quoteInterval = setInterval(() => {
      setCurrentQuoteIndex((prevIndex) => (prevIndex + 1) % quotes.length);
    }, 6000);
    return () => clearInterval(quoteInterval);
  }, [quotes.length]);

  useEffect(() => {
    // Check for reset parameter
    if (searchParams.get('reset') === 'true') {
      localStorage.removeItem('streamAccess');
      localStorage.removeItem('pendingSessionId');
      localStorage.removeItem('pendingPaymentEmail');
      window.history.replaceState({}, '', '/');
    }
    
    // Clear any old pending payment state on page load
    localStorage.removeItem('pendingSessionId');
    localStorage.removeItem('pendingPaymentEmail');
    
    const checkExistingAccess = async () => {
      try {
        const stored = localStorage.getItem('streamAccess');
        
        if (!stored) {
          setCheckingAccess(false);
          return;
        }

        const storedData = JSON.parse(stored);
        
        // Validate with backend
        const response = await fetch(`${API_URL}/api/stream/validate-access`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${storedData.accessToken}`
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.valid) {
            setHasAccess(true);
          } else {
            // Only remove if explicitly invalid (expired)
            localStorage.removeItem('streamAccess');
          }
        } else if (response.status === 401 || response.status === 403) {
          // Only remove on auth errors, not network errors
          localStorage.removeItem('streamAccess');
        }
        // For other errors (500, network), keep the token and let user try again
      } catch (error) {
        // Network error - DON'T remove the token, might just be temporary
        // Still show continue watching if we have a stored token
        const stored = localStorage.getItem('streamAccess');
        if (stored) {
          setHasAccess(true);
        }
      } finally {
        setCheckingAccess(false);
      }
    };

    checkExistingAccess();
  }, [searchParams]);

  const handleStreamNowClick = () => {
    if (hasAccess) {
      navigate('/stream');
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <div className="landing-page" data-testid="landing-page">
      {/* Full Page Background Video */}
      <div className="landing-page-background-video">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="background-video-element"
          data-testid="background-video"
        >
          <source 
            src="https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/hn3j6uvs_Sequence%2001.mp4" 
            type="video/mp4" 
          />
        </video>
      </div>
      <div className="landing-page-overlay"></div>
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="title-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_washed-away-cinema/artifacts/zlh4k7vd_LOGO.png" 
              alt="Washed Away - The Beating Heart Behind Australia's Worst Flood"
              className="hero-logo"
            />
            <h1 className="hero-headline" data-testid="hero-headline">
              In 24 hours, they lost everything.<br />
              <span className="hero-headline-accent">What they did next will stay with you forever.</span>
            </h1>
            <p className="hero-subheading" data-testid="hero-subheading">
              Washed Away is the true story of the Northern Rivers &mdash; Australia's worst flood disaster &mdash; told by the survivors, volunteers, and neighbours who refused to give up.
            </p>
          </div>

          {/* Trailer Embed - Between description and quote */}
          <div className="trailer-container">
            <div className="trailer-wrapper">
              <iframe
                src="https://player.vimeo.com/video/1149954429?autoplay=1&muted=1&loop=0&badge=0&autopause=0&player_id=0&app_id=58479&api=1&title=0&byline=0&portrait=0"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                title="Washed Away Trailer"
                className="trailer-iframe"
                id="trailer-video"
              ></iframe>
            </div>
            <p className="hero-credits" data-testid="hero-credits">
              Narrated by David Field. Featuring music from Silverchair, Tex Perkins, Ziggy Alberts, Pete Murray, Goanna, Noiseworks &amp; more.
            </p>
          </div>

          {/* Rotating Quotes */}
          <div className="quotes-slider" data-testid="quotes-slider">
            <div className="quote-container">
              <p className="hero-tagline" key={currentQuoteIndex}>
                "{quotes[currentQuoteIndex].text}"
                <span className="tagline-attribution">
                  — {quotes[currentQuoteIndex].author}, {quotes[currentQuoteIndex].title}
                </span>
              </p>
            </div>
            <div className="quote-dots">
              {quotes.map((quote, index) => (
                <button
                  key={quote.id}
                  className={`quote-dot ${index === currentQuoteIndex ? 'active' : ''}`}
                  onClick={() => setCurrentQuoteIndex(index)}
                  aria-label={`Go to quote by ${quote.author}`}
                />
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="cta-section">
            {hasAccess && (
              <div className="access-active-notice">
                <Shield className="h-5 w-5" />
                <span>You have active access!</span>
              </div>
            )}
            <div className="dual-cta-buttons">
              <Button
                onClick={handleStreamNowClick}
                className={`stream-cta-button primary-cta ${hasAccess ? 'has-access' : ''}`}
                size="lg"
                disabled={checkingAccess}
                data-testid="stream-now-button"
              >
                <Play className="mr-2 h-5 w-5" />
                {checkingAccess ? 'Checking...' : hasAccess ? 'Continue Watching' : 'Stream Now · $12.69'}
              </Button>
              {!hasAccess && (
                <Button
                  onClick={() => {
                    pauseAllVideos();
                    setIsPreviewOpen(true);
                  }}
                  className="preview-cta-button secondary-cta"
                  size="lg"
                  variant="outline"
                  data-testid="preview-button"
                >
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="5 3 19 12 5 21 5 3" fill="currentColor"/>
                  </svg>
                  Watch 8 Mins Free
                </Button>
              )}
            </div>
            {!hasAccess && (
              <p className="cta-subtext">48-hour access · Watch on any device</p>
            )}
          </div>

          {/* Impact & Testimonials Side-by-Side Section */}
          <div className="impact-testimonials-grid">
            {/* Impact Section - Charity Partnership */}
            <div className="impact-section" data-testid="impact-section">
              <div className="impact-content">
                <div className="impact-heart-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="heart-svg">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                </div>
                <h3 className="impact-title">50% of Profits Rebuild Homes</h3>
                <p className="impact-description">
                  Supporting the Hands & Hearts Project to rebuild homes for Northern Rivers flood survivors
                </p>
                <a 
                  href="https://www.mrnc.com.au/pages/hands-hearts-project" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="impact-partner"
                >
                  <span className="partner-label">Proudly supporting</span>
                  <div className="partner-logos">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_628a7b07-3686-4354-97ee-5136608ab8a9/artifacts/acngob4i_mrnc.png"
                      alt="MRNC - Mid Richmond Neighbourhood Centre"
                      className="mrnc-logo"
                    />
                    <img 
                      src="https://customer-assets.emergentagent.com/job_628a7b07-3686-4354-97ee-5136608ab8a9/artifacts/qfyairm9_HANDSandHEARTProject.png"
                      alt="Hands & Hearts Project"
                      className="hands-hearts-logo"
                    />
                  </div>
                  <span className="learn-more-link">Learn More →</span>
                </a>
              </div>
            </div>

            {/* Testimonials CTA - Same size as Impact */}
            <TestimonialsCTA 
              onClick={() => {
                pauseAllVideos(); // Pause trailer and background videos
                setIsTestimonialsOpen(true);
              }} 
              backgroundImage="https://customer-assets.emergentagent.com/job_1e65f66a-5da4-4fcc-97cf-ce6020929cc1/artifacts/o8tkurxv_Shelly-hay-rotawing.png"
            />
          </div>

          {/* Stories Section - Replaces Image Slider */}
          <StoriesPreview />

          {/* Secondary CTA after slider */}
          <div className="secondary-cta-section">
            <Button
              onClick={handleStreamNowClick}
              className={`stream-cta-button ${hasAccess ? 'has-access' : ''}`}
              size="lg"
              data-testid="stream-now-button-secondary"
            >
              <Play className="mr-2 h-5 w-5" />
              {hasAccess ? 'Continue Watching' : 'Stream Now · $12.69'}
            </Button>
            {!hasAccess && (
              <p className="cta-subtext">48-hour access · Watch on any device</p>
            )}
          </div>
        </div>
      </section>

      {/* Payment Modal */}
      <PaymentModal 
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          // Check if a payment session was created (modal will set this)
          if (localStorage.getItem('pendingSessionId')) {
            setPaymentPending(true);
          }
        }}
        onSuccess={() => {
          setIsModalOpen(false);
          setHasAccess(true);
          navigate('/stream');
        }}
      />

      {/* Testimonials Modal */}
      <TestimonialsModal 
        isOpen={isTestimonialsOpen}
        onClose={() => {
          setIsTestimonialsOpen(false);
          resumeBackgroundVideo(); // Resume background video when closing
        }}
      />

      {/* Free Flow Films Shoutout */}
      <section className="fff-section">
        <div className="fff-content">
          <p className="fff-shoutout">
            "To the incredibly talented, passionate creatives who poured their heart and soul into this project from across Australia — what you delivered is something we are all profoundly proud of. Your contribution will never be forgotten, and now we get to share it with the world."
          </p>
          <a 
            href="https://www.imdb.com/title/tt39048607/?ref_=nv_sr_srsg_1_tt_8_nm_0_in_0_q_washedaway"
            target="_blank"
            rel="noopener noreferrer"
            className="imdb-link"
          >
            Check out all the cast and production team that went into producing the award-winning Washed Away →
          </a>
          <img 
            src="https://customer-assets.emergentagent.com/job_628a7b07-3686-4354-97ee-5136608ab8a9/artifacts/vv5qi4pk_FFF_TEXT_BIRD_LOGO_WHITE.png"
            alt="Free Flow Films"
            className="fff-logo"
          />
          {/* Security Badges moved here */}
          {!hasAccess && (
            <div className="security-badges fff-badges">
              <div className="badge-item">
                <Shield className="h-4 w-4" />
                <span>256-bit SSL Secured</span>
              </div>
              <div className="badge-item">
                <Lock className="h-4 w-4" />
                <span>Powered by Stripe</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <p className="footer-text">
            © 2025 Washed Away. All rights reserved.
          </p>
          <div className="footer-links">
            <a href="/terms" className="footer-link">
              Terms & Conditions
            </a>
            {hasAccess && (
              <button 
                onClick={() => {
                  localStorage.removeItem('streamAccess');
                  localStorage.removeItem('pendingSessionId');
                  localStorage.removeItem('pendingPaymentEmail');
                  setHasAccess(false);
                  setPaymentPending(false);
                  window.location.reload();
                }}
                className="footer-link reset-link"
              >
                Reset Access (Testing)
              </button>
            )}
          </div>
        </div>
      </footer>

      {/* Sticky Mobile CTA - hide when modal is open, show green for users with access */}
      {!isModalOpen && !isPreviewOpen && (
        <div className="sticky-mobile-cta">
          <Button
            onClick={handleStreamNowClick}
            className={`sticky-cta-button ${hasAccess ? 'has-access' : ''}`}
            data-testid="sticky-mobile-cta"
          >
            <Play className="mr-2 h-4 w-4" />
            {hasAccess ? 'Continue Watching' : 'Stream Now · $12.69'}
          </Button>
          {!hasAccess && (
            <button
              onClick={() => {
                pauseAllVideos();
                setIsPreviewOpen(true);
              }}
              className="sticky-preview-link"
              data-testid="sticky-preview-link"
            >
              Watch 8 mins free →
            </button>
          )}
        </div>
      )}

      {/* 8 Minute Preview Modal */}
      {isPreviewOpen && (
        <div 
          className="preview-modal-overlay"
          onClick={() => {
            setIsPreviewOpen(false);
            resumeBackgroundVideo();
          }}
          data-testid="preview-modal"
        >
          <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="preview-modal-close"
              onClick={() => {
                setIsPreviewOpen(false);
                resumeBackgroundVideo();
              }}
              aria-label="Close preview"
            >
              ✕
            </button>
            <div className="preview-modal-video">
              <iframe
                src="https://player.vimeo.com/video/1185618783?autoplay=1&title=0&byline=0&portrait=0"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Watch 8 Minutes Free - Washed Away Preview"
              ></iframe>
            </div>
            <div className="preview-modal-cta">
              <p className="preview-modal-text">Like what you see?</p>
              <Button
                onClick={() => {
                  setIsPreviewOpen(false);
                  resumeBackgroundVideo();
                  setIsModalOpen(true);
                }}
                className="preview-stream-button"
                data-testid="preview-stream-button"
              >
                <Play className="mr-2 h-4 w-4" />
                Stream Full Documentary · $12.69
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
